// shared-types/src/analytics/types.ts - Analytics types for tracking and reporting

import { BaseEntityWithAccount } from '../common'

/**
 * Traffic source types
 */
export enum TrafficSource {
    DIRECT = 'direct',
    ORGANIC = 'organic',
    PAID = 'paid',
    REFERRAL = 'referral',
    SOCIAL = 'social',
    EMAIL = 'email',
    OTHER = 'other',
}

/**
 * Device types
 */
export enum DeviceType {
    DESKTOP = 'desktop',
    MOBILE = 'mobile',
    TABLET = 'tablet',
}

/**
 * Page types for categorization
 */
export enum PageType {
    HOME = 'home',
    CATALOG = 'catalog',
    PRODUCT = 'product',
    CART = 'cart',
    CHECKOUT = 'checkout',
    ORDER_CONFIRMATION = 'order_confirmation',
    PAGE = 'page',
    SEARCH = 'search',
    OTHER = 'other',
}

/**
 * Event types for analytics tracking
 */
export enum AnalyticsEventType {
    // Session events
    SESSION_START = 'session_start',
    SESSION_END = 'session_end',

    // Page events
    PAGE_VIEW = 'page_view',

    // Product events
    PRODUCT_VIEW = 'product_view',
    PRODUCT_LIST_VIEW = 'product_list_view',
    PRODUCT_SEARCH = 'product_search',
    SEARCH_SUGGESTION_CLICK = 'search_suggestion_click',
    PRODUCT_SELECT = 'product_select',
    PRODUCT_SHARE = 'product_share',

    // Cart events
    ADD_TO_CART = 'add_to_cart',
    REMOVE_FROM_CART = 'remove_from_cart',
    UPDATE_CART_QUANTITY = 'update_cart_quantity',
    CART_ITEM_ADDED = 'cart_item_added',
    CART_ITEM_REMOVED = 'cart_item_removed',
    CART_ITEM_QUANTITY_UPDATED = 'cart_item_quantity_updated',

    // Checkout events
    CHECKOUT_START = 'checkout_start',
    CHECKOUT_STEP = 'checkout_step',
    CHECKOUT_COMPLETE = 'checkout_complete',
    CHECKOUT_VIEW = 'checkout_view',
    CHECKOUT_ADD_CUSTOMER_INFO = 'checkout_add_customer_info',
    CHECKOUT_ADD_PAYMENT_INFO = 'checkout_add_payment_info',
    CHECKOUT_ADD_SHIPPING_INFO = 'checkout_add_shipping_info',
    CHECKOUT_ADD_BILLING_INFO = 'checkout_add_billing_info',
    CHECKOUT_CONFIRMED = 'checkout_confirmed',

    // Conversion events
    PURCHASE = 'purchase',

    // Engagement events
    CLICK = 'click',
    SCROLL = 'scroll',
}

/**
 * Funnel steps for sales funnel analysis
 */
export enum FunnelStep {
    VISIT = 'visit',
    PRODUCT_VIEW = 'product_view',
    ADD_TO_CART = 'add_to_cart',
    CHECKOUT_START = 'checkout_start',
    CHECKOUT_COMPLETE = 'checkout_complete',
    PURCHASE = 'purchase',
}

/**
 * Analytics Session - Tracks a user visit session
 */
export interface AnalyticsSession extends BaseEntityWithAccount {
    visitorId: string
    cartId?: string
    customerId?: string

    // Timing
    startedAt: Date
    endedAt?: Date
    duration?: number // in seconds
    lastActivityAt: Date

    // Traffic source
    source: TrafficSource
    medium?: string
    campaign?: string
    content?: string
    term?: string

    // Referrer info
    referrer?: string
    referrerDomain?: string

    // Pages
    landingPage: string
    exitPage?: string
    pageviews: number
    events: number

    // Device info
    device: DeviceType
    browser?: string
    browserVersion?: string
    os?: string
    osVersion?: string
    screenWidth?: number
    screenHeight?: number

    // Location (from IP)
    country?: string
    region?: string
    city?: string

    // Conversion
    isConverted: boolean
    conversionValue: number
    orderId?: string
}

/**
 * Analytics Event - Individual user action
 */
export interface AnalyticsEvent extends BaseEntityWithAccount {
    sessionId: string
    visitorId: string

    // Event info
    eventType: AnalyticsEventType
    eventData?: Record<string, any>

    // Page context
    pageUrl: string
    pageTitle?: string
    pageType?: PageType

    // Product context (for product events)
    productId?: string
    productVariantId?: string
    productSku?: string
    categoryId?: string

    // Cart/Order context (for conversion events)
    cartId?: string
    orderId?: string

    // Value
    value?: number
    currency?: string

    timestamp: Date
}

/**
 * Analytics Pageview - Page view tracking
 */
export interface AnalyticsPageview extends BaseEntityWithAccount {
    sessionId: string
    visitorId: string

    pageUrl: string
    pageTitle?: string
    pageType: PageType

    referrerUrl?: string

    timeOnPage?: number // in seconds
    scrollDepth?: number // percentage 0-100

    timestamp: Date
}

/**
 * Analytics Funnel Step - Aggregated funnel data
 */
export interface AnalyticsFunnelStep extends BaseEntityWithAccount {
    date: Date
    step: FunnelStep
    count: number
    uniqueVisitors: number
    conversionFromPrevious?: number // percentage
}

// ============================================
// DTOs for API requests
// ============================================

/**
 * DTO for creating/updating a session
 */
export interface CreateAnalyticsSessionDto {
    visitorId: string
    cartId?: string
    customerId?: string
    landingPage: string
    source: TrafficSource
    medium?: string
    campaign?: string
    content?: string
    term?: string
    referrer?: string
    referrerDomain?: string
    device: DeviceType
    browser?: string
    browserVersion?: string
    os?: string
    osVersion?: string
    screenWidth?: number
    screenHeight?: number
}

/**
 * DTO for tracking an event
 */
export interface TrackEventDto {
    sessionId: string
    visitorId: string
    eventType: AnalyticsEventType
    eventData?: Record<string, any>
    pageUrl: string
    pageTitle?: string
    pageType?: PageType
    productId?: string
    productVariantId?: string
    productSku?: string
    categoryId?: string
    cartId?: string
    orderId?: string
    value?: number
    currency?: string
}

/**
 * DTO for tracking a pageview
 */
export interface TrackPageviewDto {
    sessionId: string
    visitorId: string
    pageUrl: string
    pageTitle?: string
    pageType: PageType
    referrerUrl?: string
}

/**
 * DTO for session heartbeat (keep alive)
 */
export interface SessionHeartbeatDto {
    sessionId: string
    visitorId: string
    currentPage?: string
    scrollDepth?: number
    timeOnPage?: number
    pageviewId?: string
}

// ============================================
// Response types for dashboard
// ============================================

/**
 * Real-time analytics data
 */
export interface RealtimeAnalytics {
    activeVisitors: number
    activeSessions: ActiveSession[]
    recentEvents: RecentEvent[]
    pageviewsLastHour: number
    conversionsLastHour: number
    revenueLastHour: number
}

export interface ActiveSession {
    sessionId: string
    visitorId: string
    currentPage: string
    pageType: PageType
    device: DeviceType
    source: TrafficSource
    startedAt: Date
    pageviews: number
}

export interface RecentEvent {
    eventType: AnalyticsEventType
    pageUrl: string
    productName?: string
    value?: number
    timestamp: Date
}

/**
 * Overview analytics for a period
 */
export interface AnalyticsOverview {
    // Visitors
    totalVisits: number
    uniqueVisitors: number
    newVisitors: number
    returningVisitors: number

    // Engagement
    totalPageviews: number
    avgSessionDuration: number // in seconds
    avgPagesPerSession: number
    bounceRate: number // percentage

    // Conversions
    totalConversions: number
    conversionRate: number // percentage
    totalRevenue: number
    avgOrderValue: number

    // Comparison with previous period
    visitsPop?: number // period over period change %
    conversionsPop?: number
    revenuePop?: number
}

/**
 * Traffic sources breakdown
 */
export interface TrafficSourcesData {
    sources: TrafficSourceItem[]
    total: number
}

export interface TrafficSourceItem {
    source: TrafficSource
    visits: number
    uniqueVisitors: number
    conversions: number
    conversionRate: number
    revenue: number
    percentage: number
}

/**
 * Sales funnel data
 */
export interface FunnelData {
    steps: FunnelStepData[]
    overallConversionRate: number
}

export interface FunnelStepData {
    step: FunnelStep
    label: string
    count: number
    uniqueVisitors: number
    conversionRate: number // from previous step
    dropoffRate: number // from previous step
}

/**
 * Campaign performance
 */
export interface CampaignPerformance {
    campaigns: CampaignData[]
    total: {
        visits: number
        conversions: number
        revenue: number
    }
}

export interface CampaignData {
    campaign: string
    source: string
    medium: string
    visits: number
    uniqueVisitors: number
    conversions: number
    conversionRate: number
    revenue: number
    avgOrderValue: number
}

/**
 * Top pages data
 */
export interface TopPagesData {
    pages: PageData[]
}

export interface PageData {
    pageUrl: string
    pageTitle: string
    pageType: PageType
    views: number
    uniqueViews: number
    avgTimeOnPage: number
    bounceRate: number
    exitRate: number
}

/**
 * Product analytics
 */
export interface ProductAnalyticsData {
    products: ProductAnalyticsItem[]
}

export interface ProductAnalyticsItem {
    productId: string
    productName: string
    sku: string
    views: number
    addToCarts: number
    purchases: number
    viewToCartRate: number
    cartToPurchaseRate: number
    revenue: number
}

/**
 * Historical data point for charts
 */
export interface HistoricalDataPoint {
    date: string // ISO date string
    visits: number
    uniqueVisitors: number
    pageviews: number
    conversions: number
    revenue: number
}

/**
 * Query parameters for analytics endpoints
 */
export interface AnalyticsQueryParams {
    startDate: string // ISO date
    endDate: string // ISO date
    compareWithPrevious?: boolean
    source?: TrafficSource
    device?: DeviceType
    campaign?: string
}

/**
 * Pagination for analytics lists
 */
export interface AnalyticsPagination {
    page: number
    limit: number
    total: number
}
